import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import classnames from "classnames";
import { map } from "lodash-es";
import React, { useContext } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";

import { Modal } from "~/ui/components";
import { PremiumModalContext } from "~/ui/context/premiumModalContext";
import { useScreenLg, useScreenMd } from "~/utils";

import { playerPremiumData, teamPremiumData } from "./premiumData";

export interface PremiumModalListProps {
  title: string;
  data: React.ReactNode[];
}

const PremiumModalList: React.FC<
  PremiumModalListProps & { helperText?: React.ReactNode }
> = ({ title, data, helperText }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        borderRadius: 3,
        boxShadow: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
        px: { xs: 2, sm: 3 },
        py: 2,
        minWidth: { xs: 180, sm: 220 },
        width: "100%",
        maxWidth: { xs: 300, sm: 380 },
        transition: "box-shadow 0.2s",
        "&:hover": {
          boxShadow: 6,
        },
        mb: 2,
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color="primary"
        gutterBottom
        sx={{
          letterSpacing: "0.02em",
          mb: 1,
          pl: { xs: 1, sm: 0 },
        }}
      >
        {title}
      </Typography>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block", pl: { xs: 1, sm: 0 } }}
        >
          {helperText}
        </Typography>
      )}
      <Box
        component="ul"
        sx={{
          pl: 0,
          m: 0,
          listStyle: "none",
          color: theme.palette.text.secondary,
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.body2.fontSize,
          lineHeight: 1.7,
        }}
      >
        {map(data, (t, i) => (
          <Box
            key={i}
            component="li"
            display="flex"
            alignItems="flex-start"
            sx={{
              mb: 0.5,
              borderRadius: 1,
              px: 1,
              py: 0.5,
              transition: "background 0.2s",
              "&:hover": {
                background: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: 8,
                height: 8,
                bgcolor: theme.palette.primary.main,
                borderRadius: "50%",
                mt: "7px",
                mr: 1.5,
                flexShrink: 0,
              }}
            />
            <Box component="span">{t}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const PremiumModal: React.FC = () => {
  const theme = useTheme();
  const { open, onClose, checkboxValue, setDontShowAgain } =
    useContext(PremiumModalContext);
  const { push } = useHistory();
  const isDesktop = useScreenMd();
  const isLarge = useScreenLg();

  const modalClasses = classnames("mg-xl", isDesktop ? "w-0" : "w-120 h-150");

  const pickerWrapperStyles = isLarge
    ? {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "2rem",
        justifyContent: "center",
        alignItems: "flex-start",
      }
    : {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        alignItems: "center",
      };

  const handleDontShowAgainChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const show = e.target.checked;
    localStorage.setItem("show_again_premium", JSON.stringify(show));
    setDontShowAgain(show);
  };

  const handleBuyPremium = () => {
    push("/shop");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      bodyClassName="flex justify-center pd-sm"
      className={modalClasses}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 2, sm: 3 },
          maxHeight: { xs: "90vh", sm: "none" },
          overflowY: { xs: "auto", sm: "visible" },
          boxSizing: "border-box",
        }}
      >
        <Box sx={pickerWrapperStyles}>
          <PremiumModalList title="Player Premium" data={playerPremiumData} />
          <PremiumModalList
            title="Team Premium"
            data={teamPremiumData}
            helperText="At least two players in the squad must own Premium membership."
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            justifyContent: "space-between",
            alignItems: isDesktop ? "center" : "stretch",
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.13)}`,
            pt: 2,
            pb: 2,
            mt: 0.5,
            gap: isDesktop ? 0 : 2,
            position: "relative",
            width: "100%",
            background: "transparent",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={checkboxValue}
                onChange={handleDontShowAgainChange}
                sx={{
                  color: theme.palette.primary.main,
                  "&.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Don't show again
              </Typography>
            }
            sx={{ ml: 1 }}
          />
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: isDesktop ? 0 : 2,
              justifyContent: isDesktop ? "flex-end" : "stretch",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                px: 3,
                py: 1,
                textTransform: "none",
                borderColor: alpha(theme.palette.primary.main, 0.5),
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <FormattedMessage defaultMessage="Close" id="rbrahO" />
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBuyPremium}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                py: 1.2,
                fontSize: "1rem",
                letterSpacing: "0.03em",
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
                color: theme.palette.primary.contrastText,
                boxShadow: 4,
                transition: "transform 0.15s, box-shadow 0.15s",
                "&:hover": {
                  background: `linear-gradient(90deg, ${theme.palette.primary.light} 60%, ${theme.palette.primary.main} 100%)`,
                  boxShadow: 8,
                  transform: "scale(1.04)",
                },
              }}
            >
              <FormattedMessage defaultMessage="Buy Premium" id="i6vSWV" />
            </Button>
          </Box>
        </Box>
        <Box sx={{ pb: { xs: 3, sm: 0 } }} />{" "}
        {/* Extra bottom padding for mobile */}
      </Box>
    </Modal>
  );
};
